import React from "react";
import { Tipped } from "./Tipped";
import { Collected } from "./Collected";
import { Arrived } from "./Arrived";
import { OnTheWay } from "./OnTheWay";
import { CompleteIcon } from "./CompleteIcon";
import { Train } from "components/Train/Train";
import { useTranslation } from "react-i18next";
import type { TrainProps } from "components/Train/Train.types";
import type { IconProps } from "@clearabee/ui-library-native";

export type StepName =
  | "Accepted"
  | "On the way"
  | "Arrived"
  | "Collected"
  | "Tipped"
  | "Completed";

interface OrderTrainProps
  extends Pick<TrainProps, "vertical" | "defaultOpen" | "titleSize"> {
  step: StepName;
  minified?: boolean;
  hideDescription?: boolean;
  iconSize?: IconProps["size"];
}

const allSteps = (size: IconProps["size"]) =>
  [
    {
      icon: <CompleteIcon size={size} />,
      name: "Accepted",
      translation: "accepted",
    },
    {
      icon: <OnTheWay size={size} />,
      name: "On the way",
      translation: "onTheWay",
    },
    {
      icon: <Arrived size={size} />,
      name: "Arrived",
      translation: "arrived",
    },
    {
      icon: <Collected size={size} />,
      name: "Collected",
      translation: "collected",
    },
    {
      icon: <Tipped size={size} />,
      name: "Tipped",
      translation: "tipped",
    },
    {
      icon: <CompleteIcon size={size} />,
      name: "Completed",
      translation: "completed",
    },
  ] as const;

export const OrderTrain = ({
  step,
  minified,
  hideDescription = false,
  iconSize = "large",
  ...rest
}: OrderTrainProps): React.ReactElement => {
  const [translate] = useTranslation();
  const steps = allSteps(iconSize).filter(
    ({ name }) => !((name === "Collected" || name === "Tipped") && minified),
  );
  const mappedSteps = steps.map(({ icon, translation }) => ({
    icon,
    title: translate(`common.statusTypes.${translation}`),
    description: !hideDescription
      ? translate(`hwa.components.orderTrain.${translation}`)
      : undefined,
  }));
  const currentStep = steps.findIndex(({ name }) => name === step);

  return <Train currentStep={currentStep} steps={mappedSteps} {...rest} />;
};
